import React, { FormEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { styled } from '@portfolio/styles'
import { GTMContactFormSubmit } from '@portfolio/services/gtm-service'
import { Button } from '../Button/Button'

const CONTACT_SUBMIT_ENDPOINT = '/api/contact/submit'

const Form = styled('form', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '$20',
  padding: '0 $40',

  '@md': {
    gridTemplateColumns: '1fr 1fr',
  },
})

const Field = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',

  variants: {
    span: {
      full: {
        '@md': {
          gridColumn: '1 / -1',
        },
      },
    },
  },
})

const Label = styled('label', {
  projectFont: 'ui04',
})

const Required = styled('span', {
  color: '$errorRed',
})

const inputStyles = {
  projectFont: 'body03',
  width: '100%',
  padding: '$12 $16',
  borderRadius: '$r2',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: '$gray500_03',
  backgroundColor: 'transparent',
  color: 'currentColor',
  transition: '0.2s border-color',

  '&::placeholder': {
    color: '$gray500_06',
  },

  '&:focus-visible': {
    outline: 'none',
    borderColor: '$focusBlue',
  },

  variants: {
    theme: {
      light: {},
      dark: {
        borderColor: '$white_03',

        '&::placeholder': {
          color: '$white_06',
        },
      },
    },
  },
} as const

const Input = styled('input', inputStyles)

const Textarea = styled('textarea', {
  ...inputStyles,
  minHeight: '120px',
  resize: 'vertical',
})

const HoneypotField = styled('div', {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
})

const SubmitWrap = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',

  '@md': {
    gridColumn: '1 / -1',
  },
})

const StatusMessage = styled('p', {
  projectFont: 'body03',
  padding: '$32 $40',

  variants: {
    tone: {
      success: {},
      error: {
        color: '$errorRed',
      },
    },
  },
})

export type ContactFormProps = {
  theme?: 'light' | 'dark'
  /** GTM event context. The CRM-bound form context is `crmContext`. */
  context?: string
  /** Sanity-authored form context stored on the lead in the CRM. */
  crmContext?: string
}

export const ContactForm = ({
  theme = 'light',
  context = 'ContactForm',
  crmContext,
}: ContactFormProps) => {
  const { t } = useTranslation('contactForm')
  const [status, setStatus] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle')
  const hasWarmedUp = useRef(false)

  // Boots the serverless endpoint while the visitor is still typing, so the
  // submit itself does not pay the cold start. HEAD is answered before any
  // validation and has no side effects; a failed ping changes nothing.
  const warmUpEndpoint = () => {
    if (hasWarmedUp.current) return
    hasWarmedUp.current = true
    fetch(CONTACT_SUBMIT_ENDPOINT, { method: 'HEAD' }).catch(
      (): void => undefined,
    )
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (status === 'submitting') return

    const form = event.currentTarget
    const formData = new FormData(form)
    const email = String(formData.get('email') ?? '')
    setStatus('submitting')

    try {
      const response = await fetch(CONTACT_SUBMIT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          fullName: formData.get('fullName'),
          company: formData.get('company'),
          message: formData.get('message'),
          website: formData.get('website'),
          crmContext,
          pagePath: window.location.pathname,
        }),
      })

      if (!response.ok) {
        throw new Error(`Contact form submission failed: ${response.status}`)
      }

      GTMContactFormSubmit(email, context)
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  if (status === 'success') {
    return <StatusMessage tone="success">{t('successMessage')}</StatusMessage>
  }

  return (
    <Form onSubmit={handleSubmit} onFocus={warmUpEndpoint} noValidate={false}>
      <Field>
        <Label htmlFor="contact-form-email">
          {t('emailLabel')}
          <Required aria-hidden="true">*</Required>
        </Label>
        <Input
          theme={theme}
          id="contact-form-email"
          name="email"
          type="email"
          autoComplete="email"
          required
          maxLength={300}
        />
      </Field>
      <Field>
        <Label htmlFor="contact-form-full-name">
          {t('fullNameLabel')}
          <Required aria-hidden="true">*</Required>
        </Label>
        <Input
          theme={theme}
          id="contact-form-full-name"
          name="fullName"
          type="text"
          autoComplete="name"
          required
          maxLength={300}
        />
      </Field>
      <Field span="full">
        <Label htmlFor="contact-form-company">
          {t('companyLabel')}
          <Required aria-hidden="true">*</Required>
        </Label>
        <Input
          theme={theme}
          id="contact-form-company"
          name="company"
          type="text"
          autoComplete="organization"
          required
          maxLength={300}
        />
      </Field>
      <Field span="full">
        <Label htmlFor="contact-form-message">
          {t('messageLabel')}
          <Required aria-hidden="true">*</Required>
        </Label>
        <Textarea
          theme={theme}
          id="contact-form-message"
          name="message"
          placeholder={t('messagePlaceholder') ?? ''}
          required
          maxLength={5000}
        />
      </Field>
      <HoneypotField aria-hidden="true">
        <input name="website" type="text" tabIndex={-1} autoComplete="off" />
      </HoneypotField>
      {status === 'error' && (
        <StatusMessage tone="error" css={{ padding: 0, gridColumn: '1 / -1' }}>
          {t('errorMessage')}
        </StatusMessage>
      )}
      <SubmitWrap>
        <Button
          type="submit"
          appearance={theme === 'dark' ? 'containedWhite' : 'containedBlack'}
          disabled={status === 'submitting'}
        >
          {status === 'submitting' ? t('submitting') : t('submit')}
        </Button>
      </SubmitWrap>
    </Form>
  )
}
